import React from 'react';

import { Box, Typography, alpha } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import { ClockIcon } from 'lucide-react';

import { useCodeExpiry } from 'hooks/useCodeExpiry';

interface CodeExpiryNoticeProps {
  /** `Date.now()` at the moment the code was sent, re-captured on each resend. */
  issuedAt: number | null;
  /** `OTP_CODE_TTL_SECONDS` or `TWO_FACTOR_TICKET_TTL_SECONDS`. */
  ttlSeconds: number;
  /**
   * What the person should do once it has expired, in their terms. Each
   * surface names its OWN resend control: "Resend code" on the drawers,
   * "Send a new code" in the claim flow. A generic sentence would point at a
   * button that is labelled something else.
   */
  resendLabel?: string;
  sx?: SxProps<Theme>;
}

/**
 * The deadline on a one-time code, shown where the code is typed.
 *
 * ## What this fixes
 *
 * Before it, the only thing distinguishing a code that had timed out from a
 * code typed wrongly was... nothing. Every surface rendered "Invalid code" for
 * both, which is the wrong thing to tell someone whose code WAS correct when
 * they read it and has simply aged out while they switched apps — it reads as
 * "you mistyped" and invites them to retype the same dead digits. The resend
 * button was already there; what was missing was any reason to press it.
 *
 * So this does two jobs at once, and the second is the load-bearing one:
 * it states the deadline BEFORE it passes (so the countdown is the warning),
 * and names the expiry AFTER it passes (so the failure is attributable).
 *
 * ## Why plain MUI and not the `@repo/ui` Alert
 *
 * Same constraint as `AuthErrorMessage`, which carries the full note: apps/app
 * renders inside a shadow DOM with its own emotion cache, and the `@repo/ui`
 * Alert resolves to `display: none` there — present in the DOM, invisible on
 * screen. A countdown nobody can see is worse than no countdown, because the
 * code that sets it still runs. Box/Typography + a lucide icon render
 * reliably. TODO(VRG-173): fix the Alert-in-shadow-DOM rendering instead, once
 * auth components are promoted into the shared library.
 *
 * ## Why it carries no resend button of its own
 *
 * Every surface that shows a code field already has a resend control, each
 * with its own label, placement and in-flight state — "Resend code" paired
 * with Verify on the two drawers, a text link in the claim panel, a full-width
 * secondary on the sign-in banner. Growing a button here would either
 * duplicate those or force all five into one shape, which is a layout change
 * smuggled in under a copy fix. This names the action; the surface owns it.
 */
const EXPIRED_COLOR = '#b91c1c';

export const CodeExpiryNotice = ({
  issuedAt,
  ttlSeconds,
  resendLabel = 'Request a new one',
  sx,
}: CodeExpiryNoticeProps) => {
  const { expired, formatted, active } = useCodeExpiry(issuedAt, ttlSeconds);

  // No code outstanding — the field is not on screen in a meaningful state.
  if (!active) return null;

  if (expired) {
    return (
      <Box
        role='status'
        /*
         * MUI's ARRAY form, not `{...base, ...sx}`.
         *
         * `SxProps<Theme>` is a union that legally includes an ARRAY and a
         * `(theme) => …` callback, so spreading it into an object literal is
         * unsound — and TypeScript says so (TS2769 on the whole <Box>, with a
         * baffling error about `concat` being incompatible with an index
         * signature; it is complaining that an array's methods are not CSS
         * properties). `AuthErrorMessage` spreads it and carries exactly that
         * error today, which is where this component inherited the pattern
         * from before it was corrected here.
         *
         * The array form is MUI's documented way to merge a base against an
         * `sx` prop, and it type-checks.
         */
        sx={[theme => ({
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          mb: 2,
          px: 1.5,
          py: 1.25,
          // The px value, not `borderRadius: 2` — in sx that is a MULTIPLIER of
          // theme.shape.borderRadius and renders at 2x the buttons it sits by.
          // Same trap `AuthErrorMessage` records.
          borderRadius: `${theme.shape.borderRadius}px`,
          borderStyle: 'solid',
          borderWidth: '1px',
          // !important because index.css's `#vera-shadow-root *` border-color
          // rule has ID specificity and wins the cascade otherwise — see
          // AuthErrorMessage for the measurement.
          borderColor: `${alpha(EXPIRED_COLOR, 0.28)} !important`,
          backgroundColor: 'rgba(220, 38, 38, 0.06)',
        }), ...(Array.isArray(sx) ? sx : [sx])]}
      >
        <ClockIcon
          size={18}
          strokeWidth={1.75}
          style={{ color: EXPIRED_COLOR, flexShrink: 0 }}
        />
        <Typography variant='body2' sx={{ color: EXPIRED_COLOR, lineHeight: 1.4 }}>
          That code has expired. {resendLabel}.
        </Typography>
      </Box>
    );
  }

  return (
    <Typography
      variant='body2'
      color='text.secondary'
      // `aria-live` deliberately absent: a per-second update announced by a
      // screen reader would talk over the person typing the code. The deadline
      // is read once when focus reaches it; the EXPIRED block above is the
      // state change worth announcing, and carries role="status" for exactly
      // that reason.
      sx={[{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 2, lineHeight: 1.4 }, ...(Array.isArray(sx) ? sx : [sx])]}
    >
      <ClockIcon size={15} strokeWidth={1.75} style={{ flexShrink: 0 }} />
      This code expires in {formatted}.
    </Typography>
  );
};
